import { useCallback } from 'react'
import { TablePaginationConfig } from 'antd/es/table'
import { FilterValue, SorterResult } from 'antd/es/table/interface'

/**
 * 表格操作 Hook 的配置类型
 */
export interface TableOperationsConfig {
  /** 筛选字段映射，将表格筛选字段映射到 API 参数字段 */
  filterFieldMap: Record<string, string>
  /** 默认排序字段 */
  defaultSortField?: string
  /** 排序字段映射，将表格排序字段映射到 API 排序字段 */
  sortFieldMap?: Record<string, string>
  /** 是否启用排序功能 */
  enableSorting?: boolean
  /** 是否启用筛选功能 */
  enableFiltering?: boolean
  /** 是否启用分页功能 */
  enablePagination?: boolean
}

/**
 * 表格操作 Hook 的返回值类型
 */
export interface TableOperationsReturn {
  /** 处理表格变化的方法 */
  handleTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<any> | SorterResult<any>[],
  ) => void
  /** 处理表格选择变化的方法 */
  handleTableSelectChange: (selectedRowKeys: React.Key[]) => void
  /** 处理表格展开变化的方法 */
  handleTableExpand: (expanded: boolean, record: any) => void
}

/**
 * 表格操作 Hook 的参数类型
 */
export interface UseTableOperationsParams {
  /** 当前搜索参数 */
  searchParams: any
  /** 设置搜索参数的方法 */
  setSearchParams: (params: any) => void
  /** 选中的行键 */
  selectedRowKeys?: React.Key[]
  /** 设置选中行键的方法 */
  setSelectedRowKeys?: (keys: React.Key[]) => void
  /** 展开的行键 */
  expandedRowKey?: string | number
  /** 设置展开行键的方法 */
  setExpandedRowKey?: (key: string | number) => void
  /** 表格操作配置 */
  config: TableOperationsConfig
}

/**
 * 通用的表格操作 Hook
 *
 * @param params 表格操作参数
 * @returns 表格操作方法
 *
 * @example
 * ```tsx
 * const { handleTableChange, handleTableSelectChange, handleTableExpand } = useTableOperations({
 *   searchParams,
 *   setSearchParams,
 *   selectedRowKeys,
 *   setSelectedRowKeys,
 *   expandedRowKey,
 *   setExpandedRowKey,
 *   config: {
 *     filterFieldMap: {
 *       originType: 'originType',
 *       attackType: 'attackTypeId',
 *       riskLevel: 'riskLevel',
 *       resourceLevel: 'resourceLevel',
 *     },
 *     defaultSortField: 'ResourceSortFieldUpdateTime',
 *     sortFieldMap: {
 *       updateTime: 'ResourceSortFieldUpdateTime',
 *       createTime: 'ResourceSortFieldCreateTime',
 *     },
 *     enableSorting: true,
 *     enableFiltering: true,
 *     enablePagination: true,
 *   }
 * })
 * ```
 */
export const useTableOperations = ({
  searchParams,
  setSearchParams,
  selectedRowKeys,
  setSelectedRowKeys,
  expandedRowKey,
  setExpandedRowKey,
  config,
}: UseTableOperationsParams): TableOperationsReturn => {
  const {
    filterFieldMap,
    defaultSortField = 'ResourceSortFieldUpdateTime',
    sortFieldMap = {},
    enableSorting = true,
    enableFiltering = true,
    enablePagination = true,
  } = config

  /**
   * 处理表格变化（分页、筛选、排序）
   */
  const handleTableChange = useCallback(
    (
      pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: SorterResult<any> | SorterResult<any>[],
    ) => {
      let newParams = { ...searchParams }

      // 处理分页
      if (enablePagination) {
        newParams = {
          ...newParams,
          page: pagination.current || searchParams.page,
          pageSize: pagination.pageSize || searchParams.pageSize,
        }
      }

      // 处理筛选
      if (enableFiltering) {
        const filterParams = Object.entries(filterFieldMap).reduce(
          (acc, [filterKey, paramKey]) => {
            const filterValue = filters[filterKey]
            if (filterValue?.length) {
              acc[paramKey] = filterValue as string[] | number[]
            } else {
              // 如果筛选值为空，则删除该参数
              delete acc[paramKey]
            }
            return acc
          },
          { ...searchParams.filterParams },
        )

        newParams = {
          ...newParams,
          filterParams,
        }
      }

      // 处理排序
      if (enableSorting && sorter && !Array.isArray(sorter) && sorter.field && sorter.order) {
        const sortField = sortFieldMap[sorter.field as string] || defaultSortField
        const sortOrder = sorter.order === 'ascend' ? 'SortOrderAscending' : 'SortOrderDescending'

        newParams = {
          ...newParams,
          filterParams: {
            ...newParams.filterParams,
            sortField: sortField as any,
            sortOrder: sortOrder as 'SortOrderAscending' | 'SortOrderDescending',
          },
        }
      } else {
        delete newParams.filterParams.sortField
        delete newParams.filterParams.sortOrder
      }

      setSearchParams(newParams)
    },
    [
      searchParams,
      setSearchParams,
      filterFieldMap,
      defaultSortField,
      sortFieldMap,
      enableSorting,
      enableFiltering,
      enablePagination,
    ],
  )

  /**
   * 处理表格选择变化
   */
  const handleTableSelectChange = useCallback(
    (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys?.(newSelectedRowKeys)
    },
    [setSelectedRowKeys],
  )

  /**
   * 处理表格展开变化
   */
  const handleTableExpand = useCallback(
    (expanded: boolean, record: any) => {
      if (!setExpandedRowKey) return

      const recordKey = (record as any).resourceId || (record as any).id || (record as any).key
      if (!recordKey) return

      if (expanded) {
        setExpandedRowKey(recordKey)
      } else {
        setExpandedRowKey('')
      }
    },
    [setExpandedRowKey],
  )

  return {
    handleTableChange,
    handleTableSelectChange,
    handleTableExpand,
  }
}

export default useTableOperations
